import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MembershipService } from '@app/core/services/membership.service';
import { ProfileService } from '@app/core/services/profile.service';
import { BaseSandbox } from '@app/core/store/base.sandbox';
import { CoreState } from '@app/core/store/core.slice';
import {
  Address,
  AddressUpsert,
  ApplicationLanguageResponse,
  Company,
  EmergencyContact,
  FinancialContact,
  Personal,
  ProfileOptions,
  ProfilePersonalData,
  ProfilePictureData,
} from '@app/core/types/profile/profile.types';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  map,
  switchMap,
  tap,
} from 'rxjs';
import {
  addressState,
  changeEmailState,
  changePhoneState,
  companyState,
  financialInformationContact,
  financialInformationContactState,
  getAddress,
  getCompany,
  getPersonal,
  getPicture,
  getRNumber,
  getUser,
  pictureState,
  rNumberState,
  startEmailVerification,
  startPhoneVerification,
  updateCompany,
  updateCompanyState,
  updateEmail,
  updateEmergencyContact,
  updateEmergencyContactState,
  updatePhone,
  updatePicture,
  updatePictureState,
  userState,
  verifyEmail,
  verifyEmailState,
  verifyPhone,
  verifyPhoneState,
  personalState,
  financialContactState,
  getFinancialContact,
  getEmergencyContact,
  emergencyContactState,
  deleteAccount,
  deleteAccountState
} from '../store/profile.slice';
import { Success, User } from '@app/core/types/membership/membership.types';
import { VerificationChannel } from '@app/core/types/membership/membership.types';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { FileHttpService } from '@app/core/services/file-http.service';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { EMAIL_UPDATE, PHONE_NUMBER_UPDATE } from '@app/core/core.constants';
import { LocationItem, LocationSearch } from '@app/core/types/common/common.types';
import { CommonService } from '@app/core/services/common.service';
import { NotificationService } from '@app/core/services/notification.service';
import { PermissionState, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { AppConfigService } from '@app/core/services/config.service';
import { ActionName } from '@app/actions/action.types';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';
import { PolicyService } from '@app/core/services/policy.service';
import { ShiftSettings } from '@app/shifts/shifts.model';
import { ShiftsHttpService } from '@app/shifts/services/shifts-http.service';
import { GoogleMapsService } from '@app/core/services/google-maps.service';
import { getClientInfo } from '@app/shared/utils/client-info.util';
import { FeatureFlagStore } from '@app/core/feature-flags/feature-flag.store';
import { FeatureFlags } from '@app/core/feature-flags/feature-flag.types';
import { IdentityService } from '@app/core/services/identity.service';
import { IdentityActions } from '@app/core/types/identity.types';
import { PhotoService } from '@app/core/services/photo.service';
import { SpotchecksScreen, SurveyService } from '@app/shared/services/survey.service';
import { base64ToJPG, blobToImage } from '@app/shared/utils/file.util';
import { awaitSuccessOrCancel } from '@app/shared/utils/pipe.functions';
import { ProfileLayout } from '@app/core/types/profile/profile-layout.types';
import { isSupportedBlock } from '@app/profile/utils/profile-layout.util';

@Injectable({
  providedIn: 'root',
})
export class ProfileSandbox extends BaseSandbox {
  authenticationService = inject(AuthenticationService);
  fileHttpService = inject(FileHttpService);
  recaptchaService = inject(RecaptchaService);
  commonService = inject(CommonService);
  notificationService = inject(NotificationService);
  toastSandbox = inject(ToastSandbox);
  configService = inject(AppConfigService);
  translationSandbox = inject(TranslationSandbox);
  private readonly shiftsHttpService = inject(ShiftsHttpService);
  private readonly policyService = inject(PolicyService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly identityService = inject(IdentityService);
  private readonly photoService = inject(PhotoService);
  private readonly surveyService = inject(SurveyService);

  whatsAppPhoneVerificationFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.WhatsAppPhoneVerification);

  preferredCountries = this.configService.preferredCountries;

  rNumber$: Observable<string> = this.store.select(rNumberState.data);
  rNumberLoading$: Observable<boolean> = this.store.select(rNumberState.loading);

  picture$: Observable<ProfilePictureData> = this.store.select(pictureState.data);
  pictureLoading$: Observable<boolean> = this.store.select(pictureState.loading);

  user$: Observable<User> = this.store.select(userState.data);
  userLoading$: Observable<boolean> = this.store.select(userState.loading);

  personal$: Observable<Personal> = this.store.select(personalState.data);
  personalLoading$: Observable<boolean> = this.store.select(personalState.loading);

  company$: Observable<Company> = this.store.select(companyState.data);
  companyLoading$: Observable<boolean> = this.store.select(companyState.loading);

  financialContact$: Observable<FinancialContact> = this.store.select(financialContactState.data);
  financialContactLoading$: Observable<boolean> = this.store.select(financialContactState.loading);

  emergencyContact$: Observable<EmergencyContact> = this.store.select(emergencyContactState.data);
  emergencyContactLoading$: Observable<boolean> = this.store.select(emergencyContactState.loading);

  /**
   *  @deprecated Use address Signal instead
   */
  address$: Observable<Address> = this.store.select(addressState.data);
  address = signal<Address>(null);

  updateCompanyLoading$: Observable<boolean> = this.store.select(updateCompanyState.loading);

  updatePictureLoaded$: Observable<boolean> = this.store.select(updatePictureState.loaded);
  updatePictureLoading$: Observable<boolean> = this.store.select(updatePictureState.loading);

  updateAddressLoading = signal(false);

  addressUpdatingLoaderSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  addressUpdatingLoader$: Observable<boolean> = this.addressUpdatingLoaderSubject.asObservable();

  updateEmergencyContactLoading$: Observable<boolean> = this.store.select(updateEmergencyContactState.loading);

  uploadedFinancialInformationContact$: Observable<boolean> = this.store.select(
    financialInformationContactState.loaded
  );
  uploadingFinancialInformationContact$: Observable<boolean> = this.store.select(
    financialInformationContactState.loading
  );

  profilePictureLoading$ = combineLatest([this.pictureLoading$, this.picture$, this.updatePictureLoading$]).pipe(
    map(([loading, picture, updating]) => (loading && !picture) || updating)
  );
  readonly profilePictureLoading = toSignal(this.profilePictureLoading$, { initialValue: false });

  readonly profilePictureUrl = toSignal(
    this.picture$.pipe(
      filter(Boolean),
      switchMap(async ({ picture }) => (picture instanceof Blob ? blobToImage(picture) : (picture as unknown as string)))
    ),
    { initialValue: null }
  );

  readonly user = toSignal(this.user$, { initialValue: null });

  verifyEmail$ = this.store.select(verifyEmailState.data);
  verifyEmailLoaded$ = this.store.select(verifyEmailState.loaded);
  verifyEmailLoading$ = this.store.select(verifyEmailState.loading);

  updateEmailLoaded$ = this.store.select(changeEmailState.loaded);
  updateEmailLoading$ = this.store.select(changeEmailState.loading);

  verifyPhone$ = this.store.select(verifyPhoneState.data);
  verifyPhoneLoaded$ = this.store.select(verifyPhoneState.loaded);
  verifyPhoneLoading$ = this.store.select(verifyPhoneState.loading);

  updatePhoneLoaded$ = this.store.select(changePhoneState.loaded);
  updatePhoneLoading$ = this.store.select(changePhoneState.loading);

  deleteAccountLoading$ = this.store.select(deleteAccountState.loading);
  deleteAccountSuccess$ = this.store.select(deleteAccountState.success);

  applicationLanguage = this.translationSandbox.applicationLanguage;
  updatingApplicationLanguage = signal(false);

  versionNumber = signal<string>('');

  profilePersonalDataSubject = new BehaviorSubject<ProfilePersonalData>(null);
  profilePersonalData$ = this.profilePersonalDataSubject.asObservable();
  profilePersonalDataLoadingSubject = new BehaviorSubject<boolean>(false);
  profilePersonalDataLoading$ = this.profilePersonalDataLoadingSubject.asObservable();

  updateProfilePersonalDataSubject = new BehaviorSubject<boolean>(false);
  updateProfilePersonalData$ = this.updateProfilePersonalDataSubject.asObservable();

  locationsSubject = new BehaviorSubject<LocationItem[]>([]);
  locations$ = this.locationsSubject.asObservable();

  profileOptionsSubject = new BehaviorSubject<ProfileOptions>(null);
  profileOptions$ = this.profileOptionsSubject.asObservable();

  careerOptions$ = this.profileOptions$.pipe(
    map((options) => options?.myCareer)
  );

  profileWizardOptionsSubject = new BehaviorSubject<ProfileOptions>(null);
  profileWizardOptions$ = this.profileWizardOptionsSubject.asObservable();

  profileWizardSteps = [];

  readonly pushNotificationPermissionState = new BehaviorSubject<PermissionState>('denied');
  readonly pushNotificationPermissionState$ = this.pushNotificationPermissionState.asObservable();
  readonly pushNotificationPermission = toSignal(this.pushNotificationPermissionState$, { requireSync: true });
  readonly pushNotificationsEnabled = computed(() => this.pushNotificationPermission() === 'granted');

  private readonly pushNotificationPermissionStateLoading = new BehaviorSubject(false);
  readonly pushNotificationPermissionStateLoading$ = this.pushNotificationPermissionStateLoading.asObservable();
  readonly pushNotificationPermissionLoading = toSignal(this.pushNotificationPermissionStateLoading$, { requireSync: true });

  shiftSettings = signal<ShiftSettings>(null);

  private readonly shiftSettingsLoading = new BehaviorSubject(false);
  readonly shiftSettingsLoading$ = this.shiftSettingsLoading.asObservable();

  private readonly saveShiftSettingsLoading = new BehaviorSubject(false);
  readonly saveShiftSettingsLoading$ = this.saveShiftSettingsLoading.asObservable();

  private readonly applicationLanguageSubject = new BehaviorSubject<ApplicationLanguageResponse>(null);
  readonly applicationLanguage$ = this.applicationLanguageSubject.asObservable();

  readonly privacyPolicy$ = this.policyService.getPrivacyPolicy();
  readonly termsAndConditions$ = this.policyService.getTermsAndConditions();

  readonly identityActions = signal<IdentityActions | null>(null);

  readonly profileOptions = toSignal(this.profileOptions$, { requireSync: true });
  readonly isPersonalDataReadOnly = computed(() => !!this.profileOptions()?.personalData?.isReadOnly);
  readonly isAddressReadOnly = computed(() => !!this.profileOptions()?.address?.isReadOnly);

  readonly layout = signal<ProfileLayout | null>(null);
  readonly layoutBlocks = computed(() => this.layout()?.blocks.filter(isSupportedBlock));

  constructor(
    store: Store<CoreState>,
    private membershipService: MembershipService,
    private profileService: ProfileService
  ) {
    super(store);
    this.getProfileOptions();
    this.getAppVersion();
  }

  async getAppVersion(): Promise<void> {
    const { version } = await getClientInfo();
    this.versionNumber.set(version);
  }

  getRNumber(): void {
    this.load(getRNumber, {}, this.membershipService.getExternalId());
  }

  loadPicture(): void {
    this.load(
      getPicture,
      {},
      this.profileService.getUserPicture().pipe(
        map((response) => {
          return response.body
            ? {
                picture: response.body,
                eTag: response?.headers?.get('x-hohr-etag'),
              }
            : { picture: 'assets/img/image-placeholder.svg', eTag: null };
        })
      )
    );
  }

  updatePicture(data: ProfilePictureData): void {
    this.load(updatePicture, { data }, this.profileService.setProfilePicture(data.picture, data.eTag));
  }

  async changeProfilePicture(): Promise<void> {
    const source = await this.photoService.openPhotoOptionPicker();
    const img = await this.photoService.getPhoto(source);

    if (!img) {
      return;
    }

    this.updatePicture({
      picture: base64ToJPG(img),
      eTag: (await firstValueFrom(this.picture$))?.eTag,
    });

    this.updatePictureLoading$
      .pipe(awaitSuccessOrCancel(() => this.updatePictureLoaded$))
      .subscribe(() => this.toastSandbox.showSuccessToast({ key: 'my-profile.update-picture.success' }));
  }

  trackMyProfileScreen(): void {
    this.surveyService.trackScreen(SpotchecksScreen.MyProfile);
  }

  loadLayout(): void {
    this.profileService.getLayout().subscribe((layout) => this.layout.set(layout));
  }

  loadProfilePersonalData(): void {
    this.profilePersonalDataLoadingSubject.next(true);

    this.profileService
      .getPersonalData()
      .subscribe((value: ProfilePersonalData) => {
        this.profilePersonalDataSubject.next(value);
        this.profilePersonalDataLoadingSubject.next(false);
      });
  }

  loadUser(): void {
    this.load(getUser, {}, this.membershipService.getUserProfile());
  }

  loadPersonal(): void {
    this.load(getPersonal, {}, this.profileService.getPersonal());
  }

  loadCompany(): void {
    this.load(getCompany, {}, this.profileService.getCompany());
  }

  loadFinancialContact(): void {
    this.load(getFinancialContact, {}, this.profileService.getFinancialContact());
  }

  loadEmergencyContact(): void {
    this.load(getEmergencyContact, {}, this.profileService.getEmergencyContact());
  }

  /**
   * @deprecated Use getAddress() instead (Signal approach)
   */
  loadAddress(): void {
    this.load(getAddress, {}, this.profileService.getAddress());
  }

  getAddress(): void {
    this.profileService.getAddress()
      .pipe(tap(address => this.address.set(address)))
      .subscribe();
  }

  geocodeAddress(address: string): Promise<google.maps.LatLngLiteral> {
    return this.googleMapsService.geocodeAddress(address);
  }

  updateProfilePersonalData(data: ProfilePersonalData, showSuccessToast = true): void {
    this.updateProfilePersonalDataSubject.next(true);
    this.profileService
      .updatePersonalData(data)
      .pipe(finalize(() => this.updateProfilePersonalDataSubject.next(false)))
      .subscribe((response) => {
        this.profilePersonalDataSubject.next(response);
        if (showSuccessToast) {
          this.toastSandbox.showSuccessToast({ key: 'my-profile.update-user-info.success' });
        }
      });
  }

  confirmPersonalData(): void {
    this.profileService.confirmPersonalData().subscribe();
  }

  updateCompany(data: Company): void {
    this.load(updateCompany, { data }, this.profileService.updateCompany(data));
  }

  updateAddress(data: AddressUpsert): void {
    this.updateAddressLoading.set(true);
    this.profileService.updateAddress(data)
      .pipe(
        tap(address => this.address.set(address)),
        finalize(() => this.updateAddressLoading.set(false)))
      .subscribe(() => this.toastSandbox.showSuccessToast({ key: 'my-profile.update-address.success' }));
  }

  updateAddressWithoutConfirmation(data: AddressUpsert): void {
    this.addressUpdatingLoaderSubject.next(true);
    this.profileService
      .updateAddress(data)
      .pipe(finalize(() => this.addressUpdatingLoaderSubject.next(false)))
      .subscribe();
  }

  updateFinancialInformation(contact: FinancialContact): void {
    this.load(financialInformationContact, { contact }, this.profileService.updateFinancialContact(contact));
  }

  updateEmergencyContact(data: EmergencyContact): void {
    this.load(updateEmergencyContact, { data }, this.profileService.updateEmergencyContact(data));
  }

  startEmailVerification(email: string): void {
    this.recaptchaService.execute(EMAIL_UPDATE, (captchaToken: string) =>
      this.load(startEmailVerification, { email }, this.membershipService.verifyEmailMeStart(email, captchaToken))
    );
  }

  verifyEmail(email: string, verificationCode: string): void {
    this.load(
      verifyEmail,
      { email, verificationCode },
      this.membershipService.verifyEmailMeCheck(email, verificationCode)
    );
  }

  checkEmailVerificationCode(email: string, verificationCode: string): Observable<boolean> {
    return this.membershipService.verifyEmailMeCheck(email, verificationCode);
  }

  updateEmail(email: string, verificationCode: string, eTag: string): void {
    this.load(
      updateEmail,
      { email, verificationCode, eTag },
      this.membershipService.updateEmail(email, verificationCode, eTag)
    );
  }

  startPhoneVerification(phoneNumber: string, channel: VerificationChannel): void {
    this.recaptchaService.execute(PHONE_NUMBER_UPDATE, (captchaToken: string) =>
      this.load(
        startPhoneVerification,
        { phoneNumber, channel },
        this.membershipService.verifyPhoneMeStart(phoneNumber, captchaToken, channel)
      )
    );
  }

  verifyPhone(phoneNumber: string, verificationCode: string): void {
    this.load(
      verifyPhone,
      { phoneNumber, verificationCode },
      this.membershipService.verifyPhoneMeCheck(phoneNumber, verificationCode)
    );
  }

  checkPhoneVerificationCode(phoneNumber: string, verificationCode: string): Observable<boolean> {
    return this.membershipService.verifyPhoneMeCheck(phoneNumber, verificationCode);
  }

  updatePhone(phoneNumber: string, verificationCode: string, eTag: string): void {
    this.load(
      updatePhone,
      { phoneNumber, verificationCode, eTag },
      this.membershipService.updatePhoneNumber(phoneNumber, verificationCode, eTag)
    );
  }

  resetPassword(): Observable<Success> {
    return this.membershipService.resetPassword();
  }

  loadIdentityActions(): void {
    this.identityService.getActions().subscribe((actions) => this.identityActions.set(actions));
  }

  deleteAccount(data: string): void {
    this.load(deleteAccount, { data }, this.profileService.deleteAccount(data));
  }

  logout(): void {
    this.layout.set(null);
    this.authenticationService.logout();
  }

  searchLocations(request: LocationSearch): void {
    if (request?.query?.length > 2) {
      this.commonService.searchLocations(request).subscribe((value: LocationItem[]) => {
        this.locationsSubject.next(value);
      });
    } else {
      this.locationsSubject.next([]);
    }
  }

  getProfileOptions(): void {
    this.profileService.getProfileOptions().subscribe((options) => {
      this.profileOptionsSubject.next(options);
    });
  }

  getProfileWizardOptions(): void {
    if (this.profileWizardOptionsSubject.value === null)
      this.profileService.getProfileOptions(ActionName.CompletePersonalData).subscribe((options) => {
        this.profileWizardOptionsSubject.next(options);
      });
  }

  async registerNotifications(): Promise<void> {
    await this.notificationService.registerNotifications();
  }

  async getPushNotificationPermissionState(): Promise<PermissionState> {
    this.pushNotificationPermissionStateLoading.next(true);
    const permissionState = await this.notificationService.getPushNotificationPermissionState();
    this.pushNotificationPermissionState.next(permissionState);
    this.pushNotificationPermissionStateLoading.next(false);
    return permissionState;
  }

  async requestPushNotificationPermission(): Promise<void> {
    this.pushNotificationPermissionStateLoading.next(true);
    const permissionState = await this.notificationService.requestPushNotificationPermission();
    this.pushNotificationPermissionState.next(permissionState);
    this.pushNotificationPermissionStateLoading.next(false);
  }

  openPushNotificationSettings(): Promise<{ status: boolean }> {
    return this.notificationService.openPushNotificationSettings();
  }

  watchPushNotificationPermissionState(): Promise<PluginListenerHandle> {
    return App.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive) {
        return;
      }
      const permissionState = await this.getPushNotificationPermissionState();
      if (permissionState === 'granted') {
        await this.registerNotifications();
      }
    });
  }

  getApplicationLanguage(): void {
    this.profileService.getApplicationLanguage().subscribe((applicationLanguage) => {
      this.applicationLanguageSubject.next(applicationLanguage);
    });
  }

  updateApplicationLanguage(languageId: string, cultureCode: string) {
    this.updatingApplicationLanguage.set(true);
    return this.profileService
      .updateApplicationLanguage({
        ...this.applicationLanguageSubject.value,
        applicationLanguageId: languageId,
      })
      .subscribe((response) => {
        this.applicationLanguageSubject.next(response);
        this.translationSandbox.changeLanguage(cultureCode);
        this.translationSandbox.setApplicationLanguage(response.applicationLanguage);
        this.updatingApplicationLanguage.set(false);
      });
  }

  loadShiftSettings(): void {
    this.shiftSettingsLoading.next(true);
    this.shiftsHttpService.getShiftSettings()
      .pipe(finalize(() => this.shiftSettingsLoading.next(false)))
      .subscribe((settings) => this.shiftSettings.set(settings));
  }

  saveShiftSettings(settings: ShiftSettings): void {
    this.saveShiftSettingsLoading.next(true);
    this.shiftsHttpService.saveShiftSettings(settings)
      .pipe(finalize(() => this.saveShiftSettingsLoading.next(false)))
      .subscribe(() => this.toastSandbox.showSuccessToast({ key: 'my-profile.shift-settings.success' }));
  }
}
